import React from 'react';
import { Table } from 'antd';
import  { ColumnsType, TableProps, TablePaginationConfig } from 'antd/lib/table';
import {
  DragDropContext,
  Draggable,
  DraggingStyle,
  Droppable,
  DropResult,
  NotDraggingStyle,
} from 'react-beautiful-dnd';
import './style.css';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { Pagination } from './pagination'


const DragHandlePropsContext = React.createContext<React.HTMLAttributes<HTMLElement> | null>(null);

const DragHandleCell: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const dragHandleProps = React.useContext(DragHandlePropsContext);
  return (
    <span
      className={`rbh-table-2__drag-handle${disabled ? ' rbh-table-2__drag-handle--disabled' : ''}`}
      {...(!disabled ? (dragHandleProps ?? {}) : {})}
    >
      <img src='/RBH.ico/drag-icon.svg' />
    </span>
  );
};

function reorder<T>(list: T[], startIndex: number, endIndex: number): T[] {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function reorderWithLockedSlots<T>(
  list: T[],
  startIndex: number,
  endIndex: number,
  isLocked: (item: T) => boolean
): T[] {
  if (startIndex === endIndex) return list;
  if (startIndex < 0 || endIndex < 0) return list;
  if (startIndex >= list.length || endIndex >= list.length) return list;
  if (isLocked(list[startIndex])) return list;
  if (isLocked(list[endIndex])) return list;

  const unlockedIndices: number[] = [];
  for (let i = 0; i < list.length; i += 1) {
    if (!isLocked(list[i])) unlockedIndices.push(i);
  }

  const sourcePos = unlockedIndices.indexOf(startIndex);
  const destPos = unlockedIndices.indexOf(endIndex);
  if (sourcePos < 0 || destPos < 0) return list;

  const unlockedItems = unlockedIndices.map((i) => list[i]);
  const reorderedUnlocked = reorder(unlockedItems, sourcePos, destPos);

  const out = Array.from(list);
  let p = 0;
  for (let i = 0; i < out.length; i += 1) {
    if (isLocked(out[i])) continue;
    out[i] = reorderedUnlocked[p];
    p += 1;
  }
  return out;
}

const getItemStyle = (
  style: DraggingStyle | NotDraggingStyle | undefined,
  isDragging: boolean
): React.CSSProperties => ({
  ...style,
  ...(isDragging
    ? {
      background: '#fafafa',
    }
    : null),
});

type Item<T> = T & { id: number, showDeleteIcon?: boolean, showEditIcon?: boolean, disableDragSort?: boolean }

export type RBHTable2Props<T> = TableProps<T> & {
  dataSource: Array<Item<T>>;
  actions?: {
    onEdit?: (item: T) => void;
    onDelete?: (item: T) => void;
  }
  dragSort?: {
    enable: boolean;
    onSuccess?: (nextDataSource: T[]) => void;
    onError?: () => void;
  }
};

export const RBHTable2 = <T extends object>(props: RBHTable2Props<T>) => {
  const {
    dataSource = [],
    dragSort,
    columns,
    pagination,
    onChange,
    actions,
    ...rest
  } = props;
  const [innerData, setInnerData] = React.useState<T[]>(dataSource ?? []);

  React.useEffect(() => {
    setInnerData(dataSource ?? []);
  }, [dataSource]);

  const paginationObj: TablePaginationConfig | undefined =
    pagination !== false && typeof pagination === 'object' ? (pagination as TablePaginationConfig) : undefined;
  const paginationEnabled = !!paginationObj;
  const initialCurrent = paginationEnabled ? paginationObj?.current ?? 1 : 1;
  const initialPageSize = paginationEnabled ? paginationObj?.pageSize ?? 10 : 10;
  const [currentPage, setCurrentPage] = React.useState<number>(initialCurrent);
  const [currentPageSize, setCurrentPageSize] = React.useState<number>(initialPageSize);
  const totalItems = paginationEnabled ? (paginationObj?.total ?? innerData.length) : innerData.length;

  const pageStartIndex = paginationEnabled ? (currentPage - 1) * currentPageSize : 0;

  const needClientSidePagination = React.useMemo(() => {
    if (!paginationEnabled) return false;
    if (typeof paginationObj?.total === 'number' && paginationObj.total > innerData.length) {
      // assume server side pagination, dataSource already paginated
      return false;
    }
    return true;
  }, [paginationEnabled, paginationObj?.total, innerData.length]);

  const paginatedData = React.useMemo(() => {
    if (!paginationEnabled) return innerData;
    if (!needClientSidePagination) return innerData;
    return innerData.slice(pageStartIndex, pageStartIndex + currentPageSize);
  }, [innerData, paginationEnabled, needClientSidePagination, pageStartIndex, currentPageSize]);

  const handleTableChange = React.useCallback(
    (pag: any, filters: Record<string, React.Key[] | null>, sorter: any, extra: any) => {
      if (paginationEnabled && pag) {
        if (typeof pag.current === 'number') setCurrentPage(pag.current);
        if (typeof pag.pageSize === 'number') setCurrentPageSize(pag.pageSize);
      }
      onChange?.(pag, filters, sorter, extra);
    },
    [onChange, paginationEnabled]
  );

  React.useEffect(() => {
    if (!paginationEnabled) return;
    if (typeof paginationObj?.current === 'number') setCurrentPage(paginationObj.current);
    if (typeof paginationObj?.pageSize === 'number') setCurrentPageSize(paginationObj.pageSize);
  }, [paginationEnabled, paginationObj?.current, paginationObj?.pageSize]);
  React.useEffect(() => {
    if (!paginationEnabled) return;
    const totalPages = Math.max(1, Math.ceil(totalItems / currentPageSize));
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [paginationEnabled, totalItems, currentPageSize, currentPage]);

  const onDragEnd = React.useCallback(
    (result: DropResult) => {
      if (!result.destination) return;
      if (result.destination.index === result.source.index) return;

      const sourceIndex = paginationEnabled ? pageStartIndex + result.source.index : result.source.index;
      const destIndex = paginationEnabled ? pageStartIndex + result.destination.index : result.destination.index;

      const next = reorderWithLockedSlots(
        innerData,
        sourceIndex,
        destIndex,
        (row) => !!(row as Item<T>)?.disableDragSort
      );
      if (next === innerData) return;
      setInnerData(next);
      dragSort?.onSuccess?.(next);
    },
    [innerData, dragSort?.onSuccess, pageStartIndex, paginationEnabled]
  );

  const withDefaultRender = (cols: ColumnsType<any>): ColumnsType<any> =>
    cols.map((col) => ({
      ...col,
      render: col.render ?? ((value: any) => (value !== null && value !== undefined && value !== '' ? value : '-')),
    }));

  const tableColumns: ColumnsType<any> | undefined = React.useMemo(() => {
    if (!columns) return columns as ColumnsType<any> | undefined;

    const newColumns: ColumnsType<any> = [
      ...columns,
    ];

    if (dragSort?.enable) {
      newColumns.unshift({
        key: '__rbh_drag_sort',
        dataIndex: '__rbh_drag_sort',
        title: '',
        width: 40,
        align: 'center',
        className: 'rbh-table-2__drag-sort-col',
        render: (_: unknown, record: Item<T>) => (
          <DragHandleCell disabled={!!record.disableDragSort} />
        ),
      })
    }

    let displayActionColumn = dataSource.some((item: Item<T>) => item.showEditIcon || item.showDeleteIcon);
    const actionColumn: ColumnsType<any> = [{
      key: 'actionColumns',
      title: 'Action',
      width: 40,
      align: 'center',
      className: 'rbh-table-2__drag-sort-col',
      render: (item: Item<T>) => {
        if (item.showDeleteIcon || item.showEditIcon) {
          displayActionColumn = true;
        }

        const handleClickEdit = (event: any) => {
          event.stopPropagation()
          actions?.onEdit?.(item)
        }

        const handleClickDelete = (event: any) => {
          event.stopPropagation()
          actions?.onDelete?.(item)
        }

        return (
          <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
            {item.showEditIcon && <EditOutlined style={{ color: '#9597BD' }} onClick={handleClickEdit} translate={null} />}
            {item.showDeleteIcon && <DeleteOutlined style={{ color: '#9597BD' }} onClick={handleClickDelete} translate={null} />}
          </div>
        )
      }
    }]

    if (displayActionColumn) {
      newColumns.push(...actionColumn)
    }
    return withDefaultRender(newColumns);
  }, [columns, dragSort?.enable]);

  const components: TableProps<Item<T>>['components'] | undefined = React.useMemo(() => {
    if (!dragSort?.enable) return undefined;

    const DraggableContainer: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = (containerProps) => (
      <Droppable droppableId='rbh-table-2-droppable' direction='vertical'>
        {(provided) => {
          const { children, ...restContainerProps } = containerProps;
          return (
            <tbody ref={provided.innerRef} {...provided.droppableProps} {...restContainerProps}>
              {children}
              {provided.placeholder}
            </tbody>
          );
        }}
      </Droppable>
    );

    const DraggableBodyRow: React.FC<{ 'data-row-key': number; children: React.ReactNode }> = (rowProps) => {
      const key = rowProps['data-row-key'];
      const absIndex = (innerData as Item<T>[]).findIndex((item) => item.id === key);

      if (absIndex < 0) return <tr {...rowProps}>{rowProps.children}</tr>;
      const record = (innerData as Item<T>[])?.[absIndex];
      const rowDragDisabled = !!record?.disableDragSort;

      const index = paginationEnabled ? absIndex - pageStartIndex : absIndex;
      // Row is not on the currently rendered page (antd pagination), don't wrap with Draggable.
      if (paginationEnabled && (index < 0 || index >= currentPageSize)) {
        return <tr {...rowProps}>{rowProps.children}</tr>;
      }

      return (
        <Draggable draggableId={String(key)} index={index} isDragDisabled={rowDragDisabled}>
          {(provided, snapshot) => (
            <tr
              ref={provided.innerRef}
              {...rowProps}
              {...provided.draggableProps}
              style={getItemStyle(provided.draggableProps.style, snapshot.isDragging)}
            >
              <DragHandlePropsContext.Provider value={rowDragDisabled ? null : (provided.dragHandleProps as any)}>
                {rowProps.children}
              </DragHandlePropsContext.Provider>
            </tr>
          )}
        </Draggable>
      );
    };

    return {
      body: {
        wrapper: DraggableContainer,
        row: DraggableBodyRow,
      },
    };
  }, [dragSort?.enable, innerData, currentPageSize, pageStartIndex, paginationEnabled]);


  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Table<Item<T>>
        {...(rest as TableProps<Item<T>>)}
        className={`rbh-table-2${dragSort?.enable ? ' rbh-table-2--drag-sort' : ''}`}
        components={components}
        columns={tableColumns}
        dataSource={paginatedData as Item<T>[]}
        rowKey={'id'}
        pagination={false}
        onChange={handleTableChange}
      />
      {paginationEnabled && (
        <Pagination
          totalItems={totalItems}
          currentPage={currentPage}
          pageSize={currentPageSize}
          pageSizeOptions={[10, 20, 30, 50]}
          onPageChange={(page) => {
            setCurrentPage(page);
            if (paginationObj) {
              const nextPagination: TablePaginationConfig = {
                ...paginationObj,
                current: page,
                pageSize: currentPageSize,
                total: totalItems,
              };
              onChange?.(
                nextPagination as any,
                {},
                {},
                { currentDataSource: paginatedData as Item<T>[] } as any
              );
            }
          }}
          onPageSizeChange={(size) => {
            setCurrentPage(1);
            setCurrentPageSize(size);
            const nextPagination: TablePaginationConfig = {
              ...paginationObj,
              current: 1,
              pageSize: size,
              total: totalItems,
            };
            onChange?.(
              nextPagination as any,
              {},
              {},
              { currentDataSource: paginatedData as Item<T>[] } as any
            );
          }}
        />
      )}
    </DragDropContext>
  )
};



export default RBHTable2;
